/**
 * Subscription service module for the frontend.
 *
 * This module provides interfaces and implementations for managing
 * user subscriptions (premium feature access) through Stripe integration.
 *
 * IMPORTANT: This is completely separate from the role-based permission system.
 * - Roles = Administrative permissions (who can manage the system)
 * - Entitlements = Premium features (who has paid for features)
 */

export * from './types';
export * from './mock';
export * from './api';
export * from './hooks';

import type { ISubscriptionService } from './types';
import { createMockSubscriptionService } from './mock';
import { createApiSubscriptionService } from './api';

// Global service instance
let subscriptionService: ISubscriptionService | null = null;

/**
 * Get the subscription service instance.
 * Creates it if it doesn't exist.
 *
 * @param userId - Optional user ID to initialize the service with
 *
 * @example
 * ```typescript
 * // Check if user has premium access
 * const subscriptionService = getSubscriptionService();
 * const hasPremium = await subscriptionService.hasEntitlement('premium');
 *
 * // NEVER do this:
 * // Entitlements should be checked through the subscription service
 * // not hardcoded or assumed based on other user properties
 * ```
 */
export function getSubscriptionService(userId?: string): ISubscriptionService {
  if (!subscriptionService) {
    // Use real API service by default, with mock as explicit fallback
    if (import.meta.env.VITE_USE_MOCK_SUBSCRIPTION === 'true') {
      subscriptionService = createMockSubscriptionService(userId);
      console.log('Subscription service initialized with mock implementation');
    } else {
      subscriptionService = createApiSubscriptionService(userId);
      console.log('Subscription service initialized with API implementation');
    }
  } else if (userId && 'setUserId' in subscriptionService) {
    // Update user ID if provided and service supports it
    const serviceWithUserId = subscriptionService as {
      setUserId: (userId: string) => void;
    };
    serviceWithUserId.setUserId(userId);
  }

  return subscriptionService;
}

/**
 * Example usage patterns for premium feature access in React components.
 * These examples show the correct way to check entitlements.
 */
export const SubscriptionExamples = {
  /**
   * Using the subscription hook in a component.
   *
   * @example
   * ```tsx
   * function PremiumFeature() {
   *   const { hasEntitlement, isLoading } = useSubscription();
   *
   *   if (isLoading) {
   *     return <LoadingSpinner />;
   *   }
   *
   *   if (!hasEntitlement('premium')) {
   *     return <UpgradePrompt />;
   *   }
   *
   *   return <PremiumContent />;
   * }
   * ```
   */
  componentUsage: 'See code example above',

  /**
   * Conditional rendering based on specific entitlement.
   *
   * @example
   * ```tsx
   * function FeatureList() {
   *   const { hasEntitlement } = useSubscription();
   *   const hasHeapsterLive = hasEntitlement('heapsterlive');
   *
   *   return (
   *     <div>
   *       <h2>Features</h2>
   *       <ul>
   *         <li>✓ Basic Feature</li>
   *         <li>✓ Another Basic Feature</li>
   *         <li className={!hasHeapsterLive ? 'opacity-50' : ''}>
   *           {hasHeapsterLive ? '✓' : '🔒'} HeapsterLive Feature
   *         </li>
   *       </ul>
   *       {!hasHeapsterLive && (
   *         <UpgradeButton />
   *       )}
   *     </div>
   *   );
   * }
   * ```
   */
  conditionalRendering: 'See code example above',

  /**
   * WRONG way - Never use roles for premium features.
   *
   * @example
   * ```tsx
   * // ❌ NEVER DO THIS
   * function WrongPremiumCheck() {
   *   // Don't check entitlements by hardcoding or assuming user properties
   *   return <PremiumContent />; // WRONG!
   * }
   *
   * // ✅ ALWAYS DO THIS
   * function CorrectPremiumCheck() {
   *   const { hasEntitlement } = useSubscription();
   *
   *   if (hasEntitlement('premium')) {
   *     return <PremiumContent />; // Correct! Based on subscription
   *   }
   * }
   * ```
   */
  wrongWay: 'See code example above',

  /**
   * Protected route pattern.
   *
   * @example
   * ```tsx
   * // Create a premium-only route component
   * function PremiumRoute({ children }: { children: React.ReactNode }) {
   *   const { hasEntitlement, isLoading } = useSubscription();
   *   const navigate = useNavigate();
   *
   *   useEffect(() => {
   *     if (!isLoading && !hasEntitlement('premium')) {
   *       navigate('/upgrade', {
   *         state: { from: location.pathname }
   *       });
   *     }
   *   }, [isLoading, hasEntitlement, navigate]);
   *
   *   if (isLoading) {
   *     return <LoadingSpinner />;
   *   }
   *
   *   return hasEntitlement('premium') ? <>{children}</> : null;
   * }
   *
   * // Usage in router
   * <Route path="/premium-feature" element={
   *   <PremiumRoute>
   *     <PremiumFeaturePage />
   *   </PremiumRoute>
   * } />
   * ```
   */
  protectedRoute: 'See code example above',
};
